<template>
  <!-- Live level of the mic audio the patient receives and which mic it is,
       with warnings when the saved mic isn't connected or the mic keeps
       picking up sound while the user is quiet. -->
  <div class="space-y-1" data-test="call-mic-level">
    <div class="flex items-center gap-2" :title="muted ? 'Muted' : 'Your microphone level'">
      <FontAwesomeIcon
        :icon="['fas', muted ? 'microphone-slash' : 'microphone']"
        class="text-xs w-3"
        :class="muted ? 'text-red-500' : 'text-gray-400'"
      />
      <div class="flex items-end gap-0.5 h-3" role="meter" aria-label="Microphone level" :aria-valuenow="litBars" aria-valuemin="0" :aria-valuemax="BAR_COUNT">
        <span
          v-for="i in BAR_COUNT"
          :key="i"
          class="w-1 rounded-sm"
          :class="i <= litBars ? barColor(i) : (dark ? 'bg-gray-600' : 'bg-gray-200')"
          :style="{ height: 4 + i + 'px' }"
        />
      </div>
      <span
        v-if="micName"
        class="text-xs truncate min-w-0"
        :class="dark ? 'text-gray-400' : 'text-gray-500'"
        data-test="call-mic-name"
      >{{ micName }}</span>
    </div>
    <p v-if="micStatus.missing" class="text-xs" :class="dark ? 'text-amber-300' : 'text-amber-700'" data-test="call-missing-mic-warning">
      {{ micStatus.missing }} isn't connected, so this call is using {{ micName || 'the default microphone' }}.
    </p>
    <p v-if="noisy && !muted" class="text-xs" :class="dark ? 'text-amber-300' : 'text-amber-700'" data-test="background-noise-warning">
      Your mic is picking up background noise. Move away from fans or heaters, or mute while typing.
    </p>
  </div>
</template>

<script>
import { twilioDevice } from '@/services/TwilioDeviceService'

const BAR_COUNT = 8
// The SDK emits `volume` about every 50 ms. Look at the last ~10 seconds.
const WINDOW_SIZE = 200
// If even the quietest tenth of the window is above this, the mic never goes
// quiet: something in the room is always audible. Picked from Voice Insights
// on two IHCI calls: one whose mic never dropped below ~0.17 (constant room
// noise) and one with normal pauses whose quietest 5% sat near 0.04.
const NOISE_FLOOR_THRESHOLD = 0.08

export default {
  name: 'CallMicLevel',
  props: {
    muted: { type: Boolean, default: false },
    // For dark call panels (PhoneCallPanel).
    dark: { type: Boolean, default: false },
  },
  data() {
    return { level: 0, noisy: false, BAR_COUNT, micStatus: twilioDevice.micStatus || { inUse: '', missing: '' } }
  },
  computed: {
    // The browser names the system entry "Default - <device>".
    micName() {
      return (this.micStatus.inUse || '').replace(/^(Default|Communications) - /, '')
    },
    litBars() {
      return this.muted ? 0 : Math.min(BAR_COUNT, Math.round(this.level * BAR_COUNT * 2))
    },
  },
  mounted() {
    this._samples = []
    this.onVolume = (inputVolume) => {
      this.level = inputVolume
      this._samples.push(inputVolume)
      if (this._samples.length > WINDOW_SIZE) this._samples.shift()
      if (this._samples.length === WINDOW_SIZE) {
        this.noisy = quietestTenth(this._samples) > NOISE_FLOOR_THRESHOLD
      }
    }
    twilioDevice.on('callVolume', this.onVolume)
    this.onMicStatus = (status) => { this.micStatus = status }
    twilioDevice.on('micStatusChanged', this.onMicStatus)
  },
  beforeUnmount() {
    twilioDevice.off('callVolume', this.onVolume)
    twilioDevice.off('micStatusChanged', this.onMicStatus)
  },
  methods: {
    barColor(i) {
      if (i > BAR_COUNT - 1) return 'bg-red-400'
      return 'bg-green-500'
    },
  },
}

export function quietestTenth(samples) {
  const sorted = [...samples].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 10)]
}
</script>
